/**
 * Phase 35 Session 35.2b-1 — Portable Text renderer.
 *
 * Pure framework-level renderer that takes a Sanity Portable Text
 * array OR a plain-text body and emits a React tree. Bundle-size
 * conscious: paragraph / headings / blockquote / list / bold /
 * italic / link / inlineCode / code-block / image only. Anything
 * fancier degrades to plain text.
 *
 * Design constraints:
 *   - **Accepts both shapes.** 35.1b's resolver currently flattens
 *     Sanity's Portable Text array into a plain string via
 *     `extractPortableTextString`; the in-repo MDX fallbacks ship
 *     as plain Markdown text too. The renderer detects which one
 *     it has + handles both. A future polish pass on the API can
 *     ship the raw Portable Text JSON without changing the
 *     consumer surface.
 *   - **Only inline-safe HTML.** Renderer never `dangerouslySet
 *     InnerHTML`s — every node it emits is a React element it
 *     constructed. Sanity authors can't smuggle a `<script>` tag.
 *   - **Stable React keys.** Sanity blocks carry a `_key` field
 *     that's stable across publishes; we honor it. Falls back to
 *     index when `_key` is missing.
 *
 * Deliberately doesn't ship as a class component or a hook — the
 * renderer is a pure function so consumers can call it from
 * tooltips, article readers, or any other surface uniformly.
 */
import { Fragment, type ReactNode } from 'react';

/* -------------------------------------------------------------------------
 * Sanity Portable Text shape (subset we render).
 * -----------------------------------------------------------------------*/

import type { PortableTextSpan, PortableTextBlock, PortableTextImage, PortableTextCode, PortableTextNode } from '@commerce-os/shared-types';
export type { PortableTextSpan, PortableTextBlock, PortableTextImage, PortableTextCode, PortableTextNode } from '@commerce-os/shared-types';

export type PortableTextBody = string | PortableTextNode[] | null | undefined;

/* -------------------------------------------------------------------------
 * Public API
 * -----------------------------------------------------------------------*/

export interface RenderPortableTextOptions {
  /** Optional className applied to the wrapping fragment's root
   *  paragraph(s). Most callers pass nothing — the surrounding
   *  Tooltip / article-reader styles cascade through. */
  className?: string;
  /** Override for image rendering — useful when the consumer wants
   *  to wire a CDN-aware loader instead of the default. */
  renderImage?: (image: PortableTextImage) => ReactNode;
  /** Plain-text body — when set, used in place of the default
   *  paragraph renderer. Lets the dashboard's article reader
   *  swap in a richer typographic treatment without forking the
   *  whole renderer. */
  renderPlainText?: (text: string) => ReactNode;
}

/**
 * Render a Sanity Portable Text body OR a plain-text string into
 * a React tree. Returns `null` when the body is empty / null /
 * unrecognized (consumers branch on null to render an empty state).
 */
export function renderPortableText(
  body: PortableTextBody,
  options: RenderPortableTextOptions = {},
): ReactNode {
  if (body == null) return null;

  if (typeof body === 'string') {
    return renderPlainTextBody(body, options);
  }

  if (!Array.isArray(body) || body.length === 0) return null;

  // Group consecutive list items into <ul>/<ol> wrappers.
  const grouped = groupListBlocks(body);

  return (
    <Fragment>
      {grouped.map((group, idx) => renderGroup(group, idx, options))}
    </Fragment>
  );
}

/**
 * Convenience helper for non-React consumers (audit listeners,
 * reporting aggregates) that want a plain-text projection of a
 * Portable Text body. Mirrors the `extractPortableTextString` in
 * the backend's sanity-client.service.
 */
export function portableTextToPlainString(body: PortableTextBody): string {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  if (!Array.isArray(body)) return '';
  const lines: string[] = [];
  for (const node of body) {
    if (!node || typeof node !== 'object') continue;
    if (node._type === 'block') {
      const text = (node.children ?? [])
        .map((child) => (typeof child.text === 'string' ? child.text : ''))
        .join('');
      if (text) lines.push(text);
    } else if (node._type === 'code') {
      lines.push(node.code);
    } else if (node._type === 'image' && node.alt) {
      lines.push(node.alt);
    }
  }
  return lines.join('\n');
}

/* -------------------------------------------------------------------------
 * Internals
 * -----------------------------------------------------------------------*/

function renderPlainTextBody(
  body: string,
  options: RenderPortableTextOptions,
): ReactNode {
  if (options.renderPlainText) {
    return options.renderPlainText(body);
  }
  if (!body) return null;
  // Split on blank-line separators so a paragraph break in the
  // markdown becomes a <p>. Within a paragraph, <br/> on \n.
  const paragraphs = body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length === 0) return null;
  return (
    <Fragment>
      {paragraphs.map((para, idx) => (
        <p key={idx} className={options.className}>
          {renderInlineLineBreaks(para)}
        </p>
      ))}
    </Fragment>
  );
}

function renderInlineLineBreaks(text: string): ReactNode {
  if (!text.includes('\n')) return text;
  const parts = text.split('\n');
  return parts.map((part, idx) => (
    <Fragment key={idx}>
      {part}
      {idx < parts.length - 1 ? <br /> : null}
    </Fragment>
  ));
}

type Group =
  | { kind: 'block'; node: PortableTextNode; index: number }
  | {
      kind: 'list';
      listItem: 'bullet' | 'number';
      blocks: PortableTextBlock[];
      startIndex: number;
    };

function groupListBlocks(body: PortableTextNode[]): Group[] {
  const out: Group[] = [];
  for (let i = 0; i < body.length; i++) {
    const node = body[i];
    if (!node || typeof node !== 'object') continue;
    if (node._type === 'block' && node.listItem) {
      const last = out[out.length - 1];
      if (
        last &&
        last.kind === 'list' &&
        last.listItem === node.listItem
      ) {
        last.blocks.push(node);
      } else {
        out.push({
          kind: 'list',
          listItem: node.listItem,
          blocks: [node],
          startIndex: i,
        });
      }
    } else {
      out.push({ kind: 'block', node, index: i });
    }
  }
  return out;
}

function renderGroup(
  group: Group,
  groupIdx: number,
  options: RenderPortableTextOptions,
): ReactNode {
  if (group.kind === 'list') {
    const Tag = group.listItem === 'number' ? 'ol' : 'ul';
    return (
      <Tag key={`list-${group.startIndex}`}>
        {group.blocks.map((block, blockIdx) => (
          <li key={block._key ?? `${group.startIndex}-${blockIdx}`}>
            {renderBlockChildren(block)}
          </li>
        ))}
      </Tag>
    );
  }
  return renderNode(group.node, group.index, options);
}

function renderNode(
  node: PortableTextNode,
  index: number,
  options: RenderPortableTextOptions,
): ReactNode {
  const key = node._key ?? `n-${index}`;
  switch (node._type) {
    case 'block':
      return (
        <Fragment key={key}>{renderBlock(node, options.className)}</Fragment>
      );
    case 'code':
      return (
        <pre key={key}>
          <code data-language={node.language ?? undefined}>{node.code}</code>
        </pre>
      );
    case 'image':
      if (options.renderImage) return options.renderImage(node);
      return renderDefaultImage(node, key);
    default:
      return null;
  }
}

function renderBlock(
  block: PortableTextBlock,
  className?: string,
): ReactNode {
  const children = renderBlockChildren(block);
  switch (block.style) {
    case 'h1':
      return <h1 className={className}>{children}</h1>;
    case 'h2':
      return <h2 className={className}>{children}</h2>;
    case 'h3':
      return <h3 className={className}>{children}</h3>;
    case 'h4':
      return <h4 className={className}>{children}</h4>;
    case 'h5':
      return <h5 className={className}>{children}</h5>;
    case 'h6':
      return <h6 className={className}>{children}</h6>;
    case 'blockquote':
      return <blockquote className={className}>{children}</blockquote>;
    case 'normal':
    default:
      return <p className={className}>{children}</p>;
  }
}

function renderBlockChildren(block: PortableTextBlock): ReactNode {
  const children = Array.isArray(block.children) ? block.children : [];
  const markDefs = Array.isArray(block.markDefs) ? block.markDefs : [];
  const markDefMap = new Map<string, { _type: string; href?: string }>();
  for (const def of markDefs) {
    if (def && def._key) markDefMap.set(def._key, def);
  }
  return children.map((span, idx) => renderSpan(span, idx, markDefMap));
}

function renderSpan(
  span: PortableTextSpan,
  index: number,
  markDefMap: Map<string, { _type: string; href?: string }>,
): ReactNode {
  if (!span || typeof span.text !== 'string') return null;
  const marks = Array.isArray(span.marks) ? span.marks : [];
  let node: ReactNode = span.text;

  for (const mark of marks) {
    if (mark === 'strong') {
      node = <strong>{node}</strong>;
    } else if (mark === 'em') {
      node = <em>{node}</em>;
    } else if (mark === 'code') {
      node = <code>{node}</code>;
    } else if (mark === 'underline') {
      node = <u>{node}</u>;
    } else if (mark === 'strike-through' || mark === 'strikethrough') {
      node = <s>{node}</s>;
    } else {
      const def = markDefMap.get(mark);
      if (def?._type === 'link' && def.href) {
        // Defense-in-depth: drop javascript: + data: URLs the editor
        // might paste; only allow http(s) / mailto / tel.
        const safe = isSafeUrl(def.href) ? def.href : undefined;
        node = (
          <a
            href={safe}
            target="_blank"
            rel="noopener noreferrer"
            data-broken={safe ? undefined : 'true'}
          >
            {node}
          </a>
        );
      }
    }
  }
  return <Fragment key={span._key ?? `s-${index}`}>{node}</Fragment>;
}

function renderDefaultImage(
  image: PortableTextImage,
  key: string,
): ReactNode {
  const url = image.asset?.url;
  if (!url) {
    // No URL — fall back to an alt-text placeholder so the merchant
    // sees something useful instead of a blank node.
    if (image.alt) {
      return (
        <p key={key}>
          <em>[image: {image.alt}]</em>
        </p>
      );
    }
    return null;
  }
  return (
    <img
      key={key}
      src={url}
      alt={image.alt ?? ''}
      loading="lazy"
      style={{ maxWidth: '100%', height: 'auto' }}
    />
  );
}

function isSafeUrl(href: string): boolean {
  if (!href || typeof href !== 'string') return false;
  const lower = href.trim().toLowerCase();
  return (
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('tel:') ||
    lower.startsWith('/')
  );
}
